import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { useLazyQuery } from '@apollo/client';
import { makeStyles, Tab, Tabs, TextField } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import messages from 'constants/dictionaries/messages';
import ADD_EXERCISE_NOTE from 'api/graphql/mutations/exercises/ADD_EXERCISE_NOTE';
import GET_EXERCISE_NOTE from 'api/graphql/queries/exercises/GET_EXERCISE_NOTE';
import useDebounce from 'services/common/useDebounce/useDebounce';
import useDidUpdateEffect from 'services/common/useDidUpdateEffect/useDidUpdateEffect';
import useFeatureFlags from 'hooks/useFeatureFlags/useFeatureFlags';
import logError from 'utils/logError/logError';
import ExerciseChat from 'components/ExerciseChat/ExerciseChat';

const TABS = {
  NOTE: 0,
  AI_CHAT: 1,
};

/** Survives remounts when switching treść / podpowiedź / odpowiedź (same exercise, same id). */
const persistedAiChatTabByExerciseId = new Map();

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.legacySpacing(8),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.legacySpacing(4),
    },
  },
  tabs: {
    marginBottom: theme.spacing(1),
  },
  tabContainer: {
    paddingTop: theme.spacing(4),
  },
  fullscreenNoteWrap: {
    flexShrink: 0,
    padding: theme.spacing(2, 2, 3),
    backgroundColor: theme.palette.background.paper,
  },
}));

const NOTE_LIMIT = 3000;

function ExerciseNote({ notesRef, exerciseId, fullscreenExercisePanel }) {
  const { enqueueSnackbar } = useSnackbar();
  const { isEnabled } = useFeatureFlags();
  const showAiChatTab = isEnabled(FEATURE_FLAGS.CHAT_AI);
  const textareaRef = useRef(null);
  const [tab, setTabInternal] = useState(TABS.NOTE);

  const persistAndSetTab = useCallback(
    newTab => {
      if (exerciseId) persistedAiChatTabByExerciseId.set(exerciseId, newTab);
      setTabInternal(newTab);
    },
    [exerciseId],
  );
  const [addNoteMutation] = useMutation(ADD_EXERCISE_NOTE);
  const [value, setValue] = useState('');

  const [debouncedValueToSave, setDebouncedValueToSave] = useState(null);
  const debouncedValue = useDebounce(debouncedValueToSave, 350);

  const [load, { data }] = useLazyQuery(GET_EXERCISE_NOTE, {
    fetchPolicy: 'no-cache',
  });

  const save = async () => {
    try {
      const input = {
        exerciseId,
        note: value,
      };
      await addNoteMutation({ variables: { input } });
    } catch (error) {
      enqueueSnackbar(messages.ERROR.SAVING('notatki'), { variant: 'error' });
      logError(error);
    }
  };

  useEffect(() => {
    const note = data?.getExercise.userData?.note;
    if (note) setValue(note);
  }, [data]);

  useEffect(() => {
    if (exerciseId) load({ variables: { uuid: exerciseId } });
  }, [exerciseId]);

  useLayoutEffect(() => {
    if (!exerciseId) {
      setTabInternal(TABS.NOTE);
      return;
    }
    const saved = persistedAiChatTabByExerciseId.get(exerciseId);
    setTabInternal(saved === TABS.AI_CHAT ? TABS.AI_CHAT : TABS.NOTE);
  }, [exerciseId]);

  useImperativeHandle(
    notesRef ?? null,
    () => ({
      focus: () => {
        if (showAiChatTab) {
          if (exerciseId) persistedAiChatTabByExerciseId.set(exerciseId, TABS.NOTE);
          setTabInternal(TABS.NOTE);
        }
        window.setTimeout(() => {
          const el = textareaRef.current;
          if (el && typeof el.focus === 'function') el.focus();
        }, 0);
      },
    }),
    [exerciseId, showAiChatTab],
  );

  const onChange = useCallback(({ target }) => {
    if (target.value.length > NOTE_LIMIT) {
      enqueueSnackbar(`Przekroczony limit ${NOTE_LIMIT} znaków notatki`, { variant: 'warning', preventDuplicate: true });
      return;
    }
    setValue(target.value);
    setDebouncedValueToSave(target.value);
  }, []);

  useDidUpdateEffect(() => {
    save();
  }, [debouncedValue]);

  const classes = useStyles({});

  const noteField = (
    <TextField
      fullWidth
      inputRef={textareaRef}
      label='Notatka do zadania'
      minRows={6}
      multiline
      name='note'
      onChange={onChange}
      placeholder='Dodaj notatkę, która będzie widoczna tylko dla Ciebie'
      value={value}
      variant='outlined'
    />
  );

  const fullscreenNoteField = (
    <div className={classes.fullscreenNoteWrap}>
      <TextField
        fullWidth
        label='Notatka do zadania'
        margin='none'
        minRows={4}
        multiline
        name='note-fullscreen'
        onChange={onChange}
        placeholder='Dodaj notatkę, która będzie widoczna tylko dla Ciebie'
        value={value}
        variant='outlined'
      />
    </div>
  );

  const fullscreenExercisePanelForChat =
    fullscreenExercisePanel != null ? (
      <>
        {fullscreenExercisePanel}
        {fullscreenNoteField}
      </>
    ) : null;

  if (!showAiChatTab) {
    return <div className={classes.root}>{noteField}</div>;
  }

  return (
    <div className={classes.root}>
      <Tabs
        indicatorColor='primary'
        onChange={(_, newValue) => persistAndSetTab(newValue)}
        textColor='primary'
        value={tab}
      >
        <Tab label='Notatka' />
        <Tab label='Czat AI' />
      </Tabs>
      {tab === TABS.NOTE && <div className={classes.tabContainer}>{noteField}</div>}
      {tab === TABS.AI_CHAT && (
        <div className={classes.tabContainer}>
          <ExerciseChat disabled={false} exerciseId={exerciseId} fullscreenExercisePanel={fullscreenExercisePanelForChat} />
        </div>
      )}
    </div>
  );
}

export default ExerciseNote;
